import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  fetchActions,
  selectActionsByContext,
  selectActionsByIds,
} from "./slice"
import { parseActionFilters } from "./filters"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { Header, Overview } from "../../ui/PageHeader"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"
import ActionsTable from "./ActionsTable"
import { useInterval } from "../timing/use-interval"

const LIVE_UPDATE_INTERVAL = 20000 // ms
const PAGE_SIZE = 200 // Default initial and subsequent page size

/* ActionsList renders a page containing a filter- and sort-able table
 * of all user actions logged.  */
const ActionsList = () => {
  const dispatch = useDispatch()

  // we get any matching actions
  const actionMatches = useSelector((state) =>
    selectActionsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const actions = useSelector((state) =>
    selectActionsByIds(state, actionMatches?.ids || [])
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)
  const filter = parseActionFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)

  useEffect(() => {
    dispatch(
      fetchActions({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // Poll at an interval, based on whether "Update live" is checked
  // As a hack, for non-live updates, set a long (but non-infinite, which breaks) interval, e.g. a week
  useInterval(
    () => {
      dispatch(
        fetchActions({
          filter,
          page: { offset: 0, limit: PAGE_SIZE },
          context: ["root"],
        })
      )
    },
    LIVE_UPDATE_INTERVAL,
    false
  )

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  // How many more are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    actions?.length && actionMatches?.page?.total_count
      ? actionMatches.page.total_count - actions.length
      : 50
  )

  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = actions?.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchActions({ filter, page, context: ["root"] }))
  }

  return (
    <Layout>
      <NavMain key="nav-left" />
      <Helmet>
        <title>User actions log - Astrosite</title>
      </Helmet>
      <ScrollablePage>
        {actions.length === 0 && actionMatches?._loadStatus === "loading" && (
          <Backdrop>
            <LoadingSpinner />
          </Backdrop>
        )}
        <Header>
          <Overview>
            <h1>Audit log</h1>
            <p>
              {actions.length > 0 && (
                <span>
                  {actionMatches?._loadStatus === "loading" && (
                    <LoadingSpinnerInline />
                  )}
                  Showing <strong>{actionMatches?.page?.end_index}</strong>
                  {actionMatches?.page?.total_count && (
                    <span>
                      {" "}
                      of{" "}
                      <strong className="count total_count">
                        {actionMatches.page.total_count}
                      </strong>
                    </span>
                  )}{" "}
                  actions
                </span>
              )}
            </p>
          </Overview>
        </Header>
        {actionMatches?._loadStatus == "rejected" && (
          <ErrorMsg
            msg={"There was a problem fetching user actions"}
            showIcon={true}
          />
        )}
        <Paper>
          <ActionsTable
            actions={actions}
            filter={filter}
            setFilter={setFilter}
          />
          {actions?.length == 0 && actionMatches?._loadStatus == "idle" && (
            <p>There are no user actions matching your criteria.</p>
          )}
          <footer className="actions">
            {actions.length > 0 &&
              actions.length !== actionMatches?.page?.total_count && (
                <>
                  <button
                    onClick={handleLoadMore}
                    disabled={actionMatches?._loadStatus == "loading"}
                  >
                    {actionMatches?._loadStatus == "loading"
                      ? "Loading..."
                      : `Show ${loadableCount} more`}
                  </button>
                </>
              )}
          </footer>
        </Paper>
      </ScrollablePage>
    </Layout>
  )
}

export default ActionsList
